import api from '@/lib/api';
import type { OnlineCheckoutSettings } from '@pms/shared';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface PropertySettings {
  id: string;
  name: string;
  currency: string;
  /** Tax % on the Room Bill subtotal (numeric column → string). */
  roomTaxRate: string;
  /** Tax % on the Food Bill subtotal (numeric column → string). */
  foodTaxRate: string;
  tagLine: string | null;
  phone: string | null;
  email: string | null;
  fax: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zipCode: string | null;
  logoUrl: string | null;
  images: string[];
  checkInTime: string | null;
  checkOutTime: string | null;
  petPolicy: string | null;
  smokingPolicy: string | null;
  houseRules: string | null;
  cancellationPolicy: string | null;
  policies: Record<string, string> | null;
  alternateLanguage: Record<string, string> | null;
}

export interface UpdatePropertySettingsPayload {
  name?: string | undefined;
  currency?: string | undefined;
  roomTaxRate?: number | undefined;
  foodTaxRate?: number | undefined;
  tagLine?: string | undefined;
  phone?: string | undefined;
  email?: string | undefined;
  fax?: string | undefined;
  website?: string | undefined;
  address?: string | undefined;
  city?: string | undefined;
  state?: string | undefined;
  country?: string | undefined;
  zipCode?: string | undefined;
  logoUrl?: string | undefined;
  images?: string[] | undefined;
  checkInTime?: string | undefined;
  checkOutTime?: string | undefined;
  petPolicy?: string | undefined;
  smokingPolicy?: string | undefined;
  houseRules?: string | undefined;
  cancellationPolicy?: string | undefined;
  policies?: Record<string, string> | undefined;
  alternateLanguage?: Record<string, string> | undefined;
}

export type GuestEngagementType =
  | 'confirmation'
  | 'cancellation'
  | 'arrival'
  | 'departure'
  | 'checkout';

export interface GuestEngagementTemplate {
  id: string;
  propertyId: string;
  templateType: GuestEngagementType;
  subject: string;
  body: string;
  isEnabled: boolean;
  sources: string[];
  propertyEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateGuestEngagementPayload {
  isEnabled?: boolean | undefined;
  propertyEmail?: string | undefined;
  subject?: string | undefined;
  body?: string | undefined;
  sources?: string[] | undefined;
}

// ─── Online Check-Out settings ────────────────────────────────────────────────

export interface UpsertOnlineCheckoutPayload {
  isEnabled?: boolean | undefined;
  windowHoursBefore?: number | undefined;
  windowHoursAfter?: number | undefined;
  requireFolioSettled?: boolean | undefined;
  confirmationMessage?: string | undefined;
}

// ─── API functions ───────────────────────────────────────────────────────────

export async function fetchPropertySettings(propertyId: string): Promise<PropertySettings> {
  const { data } = await api.get(`/admin/property-settings/${propertyId}`);
  return data;
}

export async function updatePropertySettings(propertyId: string, payload: UpdatePropertySettingsPayload): Promise<PropertySettings> {
  const { data } = await api.patch(`/admin/property-settings/${propertyId}`, payload);
  return data;
}

export async function fetchGuestEngagement(propertyId: string): Promise<GuestEngagementTemplate[]> {
  const { data } = await api.get(`/admin/guest-engagement/${propertyId}`);
  return data;
}

export async function updateGuestEngagement(
  propertyId: string,
  type: GuestEngagementType,
  payload: UpdateGuestEngagementPayload,
): Promise<GuestEngagementTemplate> {
  const { data } = await api.put(`/admin/guest-engagement/${propertyId}/${type}`, payload);
  return data;
}

export async function sendTestGuestEngagement(
  propertyId: string,
  type: GuestEngagementType,
  to: string,
): Promise<{ sent: boolean }> {
  const { data } = await api.post(
    `/admin/guest-engagement/${propertyId}/${type}/test`,
    { to },
  );
  return data;
}

export async function fetchOnlineCheckout(
  propertyId: string,
): Promise<OnlineCheckoutSettings | null> {
  const { data } = await api.get(`/admin/online-checkout/${propertyId}`);
  return data;
}

export async function updateOnlineCheckout(
  propertyId: string,
  payload: UpsertOnlineCheckoutPayload,
): Promise<OnlineCheckoutSettings> {
  const { data } = await api.put(`/admin/online-checkout/${propertyId}`, payload);
  return data;
}
